<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import DrawerComponent from '@/shared/components/DrawerComponent.vue'
import { useErrorStore, type ErrorEntry } from '@/store/errorStore'
import ErrorDetailView from './ErrorDetailView.vue'
import { Bug, Trash2 } from 'lucide-vue-next'

const errorStore = useErrorStore()
const selectedEntry = ref<ErrorEntry | null>(null)
const isOpen = ref(false)
const drawerRef = ref<HTMLElement | null>(null)

// Watch isOpen — when it becomes true, reset unread count
watch(isOpen, (val) => {
  if (val) {
    errorStore.resetUnreadCount()
  }
})

// Observe the drawer tab's aria-expanded attribute to sync isOpen
let observer: MutationObserver | null = null

onMounted(() => {
  nextTick(() => {
    if (!drawerRef.value) return
    const tabButton = drawerRef.value.querySelector('button[aria-label="Toggle drawer"]')
    if (!tabButton) return

    observer = new MutationObserver(() => {
      isOpen.value = tabButton.getAttribute('aria-expanded') === 'true'
    })
    observer.observe(tabButton, { attributes: true, attributeFilter: ['aria-expanded'] })
  })
})

onUnmounted(() => {
  observer?.disconnect()
})

function statusColorClass(code: number): string {
  if (code >= 400 && code < 500) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
  if (code >= 500) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  return 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300'
}

function relativeTime(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  return `${Math.floor(diff / 3600)}h ago`
}
</script>

<template>
  <div ref="drawerRef" class="relative">
    <DrawerComponent :width="450" :height="'70vh'" :bottomOffset="'5%'" :toggleButtonHeight="40">
      <div class="flex h-full flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
          <div class="flex items-center gap-2">
            <Bug class="h-4 w-4 text-zinc-500" />
            <h3 class="text-sm font-semibold text-zinc-800 dark:text-zinc-200">API Errors</h3>
            <span
              v-if="errorStore.errorCount > 0"
              class="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300"
            >
              {{ errorStore.errorCount }}
            </span>
          </div>
          <button
            v-if="errorStore.errorCount > 0"
            class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
            @click="errorStore.clearAll()"
          >
            <Trash2 class="h-3.5 w-3.5" />
            Clear All
          </button>
        </div>

        <!-- Content area -->
        <div class="flex-1 overflow-y-auto">
          <!-- Detail view -->
          <div v-if="selectedEntry">
            <button
              class="flex w-full items-center gap-1 border-b border-zinc-200 px-4 py-2 text-xs text-zinc-500 hover:text-zinc-700 dark:border-zinc-700 dark:hover:text-zinc-300"
              @click="selectedEntry = null"
            >
              ← Back to list
            </button>
            <ErrorDetailView :entry="selectedEntry" @close="selectedEntry = null" />
          </div>

          <!-- Error list -->
          <div v-else-if="errorStore.errors.length > 0">
            <button
              v-for="entry in errorStore.errors"
              :key="entry.id"
              class="flex w-full flex-col gap-1 border-b border-zinc-100 px-4 py-3 text-left hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-800/50"
              @click="selectedEntry = entry"
            >
              <div class="flex items-center gap-2">
                <span
                  class="rounded px-1.5 py-0.5 text-xs font-bold tabular-nums"
                  :class="statusColorClass(entry.httpStatusCode)"
                >
                  {{ entry.httpStatusCode }}
                </span>
                <span class="rounded bg-zinc-100 px-1.5 py-0.5 text-xs font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                  {{ entry.httpMethod }}
                </span>
                <span class="ml-auto text-xs text-zinc-400">{{ relativeTime(entry.capturedAt) }}</span>
              </div>
              <p class="truncate text-xs text-zinc-600 dark:text-zinc-400">{{ entry.requestUrl }}</p>
              <p class="truncate text-xs text-zinc-400 dark:text-zinc-500">{{ entry.errorMessage }}</p>
            </button>
          </div>

          <!-- Empty state -->
          <div v-else class="flex h-full items-center justify-center p-8">
            <p class="text-sm text-zinc-400">No API errors captured</p>
          </div>
        </div>
      </div>
    </DrawerComponent>

    <!-- Badge overlay near the drawer tab -->
    <div
      v-if="errorStore.unreadCount > 0 && !isOpen"
      class="pointer-events-none fixed right-[36px] z-[1002]"
      :style="{ bottom: 'calc(5% + 35vh + 10px)' }"
    >
      <span class="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white shadow">
        {{ errorStore.unreadCount }}
      </span>
    </div>
  </div>
</template>
